import { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { GlobalSEO } from '@/components/GlobalSEO';
import { Toaster } from '@/components/ui/sonner';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import HomePage from '@/pages/HomePage';
import WorkPage from '@/pages/WorkPage';
import WorkDetailPage from '@/pages/WorkDetailPage';
import ServicesPage from '@/pages/ServicesPage';
import MexicoPage from '@/pages/MexicoPage';
import PostPage from '@/pages/PostPage';
import RentalsPage from '@/pages/RentalsPage';
import DirectorPage from '@/pages/DirectorPage';
import InnovationPage from '@/pages/InnovationPage';
import ClientsPage from '@/pages/ClientsPage';
import ContactPage from '@/pages/ContactPage';
import AdminLoginPage from '@/pages/AdminLoginPage';
import AdminHomePage from '@/pages/AdminHomePage';
import AdminJournalPage from '@/pages/AdminJournalPage';
import AdminArticleEditorPage from '@/pages/AdminArticleEditorPage';
import AdminCategoriesPage from '@/pages/AdminCategoriesPage';
import AdminProjectsPage from '@/pages/AdminProjectsPage';
import AdminProjectEditorPage from '@/pages/AdminProjectEditorPage';
import AdminProjectSettingsPage from '@/pages/AdminProjectSettingsPage';
import AdminSuccessStoriesPage from '@/pages/AdminSuccessStoriesPage';
import AdminClientsPage from '@/pages/AdminClientsPage';
import AdminMexicoGalleryPage from '@/pages/AdminMexicoGalleryPage';
import AdminContentSyncPage from '@/pages/AdminContentSyncPage';
import JournalPage from '@/pages/JournalPage';
import JournalArticlePage from '@/pages/JournalArticlePage';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// 404 page
const NotFound = () => (
  <div className="pt-40 pb-20 text-center min-h-[60vh] flex flex-col items-center justify-center">
    <h1 className="font-display text-6xl text-[hsl(var(--bk-fg))]">404</h1>
    <p className="font-body text-[hsl(var(--bk-fg-muted))] mt-4">Page not found.</p>
    <a href="/" className="font-body text-sm text-[hsl(var(--bk-accent))] mt-6 hover:opacity-80 transition-opacity">
      Return home
    </a>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <div className="grain-overlay">
        <BrowserRouter>
          <GlobalSEO />
          <ScrollToTop />
          <ConditionalLayout />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

// Conditional layout wrapper to hide navbar/footer on admin routes
const ConditionalLayout = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <>
      {!isAdminRoute && <Navbar />}
      <main className="min-h-screen">
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/work" element={<WorkPage />} />
        <Route path="/work/:slug" element={<WorkDetailPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/mexico" element={<MexicoPage />} />
        <Route path="/post" element={<PostPage />} />
        <Route path="/rentals" element={<RentalsPage />} />
        <Route path="/director" element={<DirectorPage />} />
        <Route path="/innovation" element={<InnovationPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/journal" element={<JournalPage />} />
        <Route path="/journal/:slug" element={<JournalArticlePage />} />
        <Route path="/admin" element={<AdminLoginPage />} />
        <Route path="/admin/home" element={<AdminHomePage />} />
        <Route path="/admin/journal" element={<AdminJournalPage />} />
        <Route path="/admin/journal/categories" element={<AdminCategoriesPage />} />
        <Route path="/admin/journal/new" element={<AdminArticleEditorPage />} />
        <Route path="/admin/journal/edit/:id" element={<AdminArticleEditorPage />} />
        <Route path="/admin/projects" element={<AdminProjectsPage />} />
        <Route path="/admin/projects/settings" element={<AdminProjectSettingsPage />} />
        <Route path="/admin/success-stories" element={<AdminSuccessStoriesPage />} />
        <Route path="/admin/clients" element={<AdminClientsPage />} />
        <Route path="/admin/mexico-gallery" element={<AdminMexicoGalleryPage />} />
        <Route path="/admin/content-sync" element={<AdminContentSyncPage />} />
        <Route path="/admin/projects/new" element={<AdminProjectEditorPage />} />
        <Route path="/admin/projects/edit/:id" element={<AdminProjectEditorPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </main>
    {!isAdminRoute && <Footer />}
    <Toaster position="bottom-right" />
    </>
  );
};

export default App;
